/**
 * Small dialog system — canonical chrome for compact confirmation dialogs.
 *
 * Follows the regular Modal spacing with small changes: tighter radius (rounded-2xl),
 * a hairline header divider, and pill action buttons matching the add/save button specs.
 *
 * Usage:
 *   <SmallDialog title="Remove widget?" ref={hotkeyRef}>
 *     <p>...body copy...</p>
 *
 *     <SmallDialogActions>
 *       <SmallDialogButton variant="secondary">Cancel</SmallDialogButton>
 *       <SmallDialogButton variant="danger">Delete</SmallDialogButton>
 *     </SmallDialogActions>
 *   </SmallDialog>
 *
 * Mount inside a <ModalPortal size="mini"> so the dialog gets its 384px max width.
 */
import React, { forwardRef } from 'react';

import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const SmallDialog = forwardRef(({ title, children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={cn(
        'pointer-events-auto relative flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-white bg-gray-200 shadow-modal focus:outline-none dark:border-white/3',
        className,
      )}
      {...props}
    >
      {title && (
        <div className="px-5 pt-5">
          <h2 className="truncate text-base font-medium text-black dark:text-neutral-200">{title}</h2>
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
});
SmallDialog.displayName = 'SmallDialog';

const SmallDialogActions = ({ children, className, ...props }) => {
  return (
    <div className={cn('mt-9 flex justify-end gap-2', className)} {...props}>
      {children}
    </div>
  );
};

const smallDialogButtonVariants = cva(
  'flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full px-3 text-sm leading-[18px] font-medium disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-idle-blue text-white hover:opacity-90',
        secondary: 'bg-gray-100 text-gray-500 hover:bg-white dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700',
        danger: 'bg-red-600 text-white hover:bg-red-700',
      },
    },
    defaultVariants: {
      variant: 'secondary',
    },
  },
);

const SmallDialogButton = forwardRef(({ children, className, variant, ...props }, ref) => {
  return (
    <button type="button" {...props} ref={ref} data-app-region="none" className={cn(smallDialogButtonVariants({ variant }), className)}>
      {children}
    </button>
  );
});
SmallDialogButton.displayName = 'SmallDialogButton';

export { SmallDialog, SmallDialogActions, SmallDialogButton, smallDialogButtonVariants };
